import { Link, useParams } from "react-router";
import { getConfig } from "../config";
import { useLive } from "../live/channel";
import { LiveView } from "../live/LiveView";
import { ActivityEditor } from "../live/ActivityEditor";
import { SmsInbox } from "../live/SmsInbox";
import { ScreenCodeManager } from "../live/ScreenCodeManager";
import {
  gesturesFor,
  hasBeenPlayed,
  phaseGestures,
  rehearsalOf,
  useActivities,
  movedAmong,
  offeredType,
  ofType,
  rowResults,
  splitActivities,
  typesPresent,
  unfoldedAfter,
  unfoldedAtFirst,
  useRemoveActivity,
  useReorderActivities,
  useResetLive,
  useSetPhase,
  useSetState,
  useSmsInbox,
  type ControlActivity,
  type RowPlay,
} from "../live/control";
import { type ActivityType, type Results } from "../live/types";
import { Tally } from "../live/Results";
import { countByBucket, moderationPath } from "../live/moderation";
import { refusalOf } from "../live/answer";
import { withContext } from "./registry";
import { Button } from "../ui/Button";
import { Confirm } from "../ui/Confirm";
import { Menu, MenuItem, MenuSep } from "../ui/Menu";
import { Modal } from "../ui/Modal";
import { useEffect, useId, useState } from "react";

/**
 * ═══ LA RÉGIE ═══
 *
 * L'écran depuis lequel un organisateur décide de ce que huit cents personnes
 * voient, à la seconde près. Il n'apparaît jamais dans la navigation d'un
 * participant, et le serveur le refuse à qui n'a pas de rôle sur le domaine.
 *
 * ⚠️ CE QUE LA V1 ÉPARPILLE SUR ONZE ENDROITS TIENT ICI. Neuf onglets du
 * back-office historique (liste des votes, module vote, résultats, droits, module
 * question, liste des sondages, module sondage, liste des nuages, module nuage),
 * plus un écran du front (`/live/questions`), plus un basculeur de version dans le
 * back-office actuel. Un organisateur qui pilote une session passait son temps à
 * chercher l'onglet.
 *
 * ⚠️ « LANCER » EST UN SEUL APPEL, pas trois. Ouvrir l'activité, la désigner comme
 * active et basculer la salle dessus se font dans une transaction côté serveur
 * (`makeActive`) : en trois requêtes, deux opérateurs qui cliquent en même temps
 * laissent la salle sur une activité qui n'est pas celle qui vient de s'ouvrir.
 *
 * Displayed strings are French: this is a French product.
 */

const TYPE_LABELS: Record<string, string> = {
  poll: "Sondage",
  quiz: "Quiz",
  ballot: "Vote",
  questions: "Questions du public",
  cloud: "Nuage de mots",
  leaderboard: "Classement",
};

const PHASE_LABELS: Record<string, string> = {
  draft: "Préparée",
  open: "Ouverte",
  closed: "Close",
};

export function LiveRegie() {
  const {
    data: live, isPending, isError, sequenceId,
  } = useLive();

  /* La préparation ouvre une boîte : la liste reste la vue principale, et un
     formulaire de dix champs posé dessous la repoussait hors de l'écran. */
  const [preparer, setPreparer] = useState(false);
  /* L'activité en cours de modification, ou rien. */
  const [editing, setEditing] = useState<ControlActivity | null>(null);
  /**
   * ⚠️ LE GROUPE REGARDÉ, ET IL S'OUVRE SUR CE QUI RESTE À FAIRE. Une session en
   * compte une dizaine : la moitié est derrière soi au bout d'une heure, et elle
   * pousse hors de l'écran les trois lignes qu'on pilote encore.
   */
  const [showDone, setShowDone] = useState(false);
  /* Le type regardé, `""` pour tous. Une vue, pas un réglage : elle ne se garde pas
     d'une session à l'autre et ne change rien à ce que la salle voit. */
  const [type, setType] = useState<ActivityType | "">("");
  /* Ce que la colonne de commandes pilote : les activités, ou les SMS reçus. */
  const [mode, setMode] = useState<"activities" | "sms">("activities");
  /* Lue dans les deux modes : son compte sur l'onglet appelle l'œil pendant qu'on
     pilote les activités. Même cache que la liste du mode SMS, une seule lecture. */
  const sms = useSmsInbox();
  const { data: activities, isError: activitiesFailed } = useActivities();
  const setState = useSetState();
  const reorder = useReorderActivities();
  /* « Réinitialiser la répétition » : la question posée avant, et le geste. */
  const [confirmRehearsal, setConfirmRehearsal] = useState(false);
  const resetAll = useResetLive();

  /* Les types que l'événement porte, et le filtre ramené à ceux-là : voir
     `offeredType`, un filtre dont la commande a disparu ne se lève plus. */
  const types = typesPresent(activities ?? []);
  const shownType = offeredType(type, types);
  /* Ce que « Réinitialiser la répétition » toucherait, dit dans sa question. */
  const rehearsal = rehearsalOf(activities ?? [], live);
  /* Et l'état suit la vue, sinon le filtre oublié ressuscite le jour où quelqu'un
     recrée une activité de ce type. */
  useEffect(() => {
    if (type !== shownType) setType(shownType);
  }, [type, shownType]);

  if (isPending) {
    return (
      <section className="page">
        <p className="empty">Chargement de la régie.</p>
      </section>
    );
  }

  /**
   * ⚠️ UNE LECTURE RATÉE N'EST PAS UNE AUTORISATION. La garde testait `live &&
   * live.role !== "control"` : quand la lecture échouait, `live` valait `undefined`
   * et la console se dessinait entière, vide de tout, sur un écran où chaque geste
   * allait être refusé. Le serveur restait la vraie barrière — rien ne fuit, il
   * n'avait rien envoyé — mais l'organisateur lisait une console en état de marche
   * quelques secondes avant une plénière.
   */
  if (!live) {
    return (
      <section className="page">
        <p className="empty">
          La régie n'a pas pu être chargée. Vérifiez votre connexion, la page
          réessaie toute seule.
        </p>
      </section>
    );
  }

  /* ⚠️ LE SERVEUR DÉCIDE DU RÔLE, PAS L'ADRESSE. Un participant qui tape cette
     adresse reçoit le contenu d'un participant : on le lui dit, plutôt que de
     dessiner une console dont chaque bouton sera refusé. */
  if (live.role !== "control") {
    return (
      <section className="page">
        <p className="empty">
          Cet écran est réservé à l'organisation de l'événement.
        </p>
      </section>
    );
  }

  /* ⚠️ LE TYPE D'ABORD, LES DEUX GROUPES ENSUITE. Les comptes des onglets doivent
     décrire ce qu'on verra en cliquant dessus : filtré après, « Terminées (4) »
     annonçait quatre lignes et n'en montrait qu'une.

     Rangé sur `endedAt`, c'est-à-dire sur le geste : une activité close reste dans ce
     qui n'est pas terminé tant qu'on ne l'a pas terminée, on peut vouloir la rouvrir. */
  const groups = splitActivities(ofType(activities ?? [], shownType));
  const shown = showDone ? groups.done : groups.todo;
  /* Combien attendent un verdict : c'est le seul chiffre qui doit rappeler la file
     depuis la régie, maintenant qu'elle n'y est plus. Il se lit sur la ligne à
     l'écran, dans son « Modérer (n) ». */
  const waiting = countByBucket(live.answers ?? []).received;
  /* L'activité à l'écran porte-t-elle des questions : seul son compte arrive en direct. */
  const questionsOnScreen = live.activity?.type === "questions";

  return (
    <section className="page rgp">
      {/**
       * ═══ CE QUE VOIT LA SALLE, ET LES COMMANDES À CÔTÉ ═══
       *
       * ⚠️ LE MÊME COMPOSANT QUE LA PAGE DU PARTICIPANT, pas une copie « pour
       * l'aperçu ». Une seconde version aurait divergé au premier changement, et
       * la divergence se serait vue un jour de plénière : l'organisateur pilote ce
       * qu'il croit voir. `LiveView` est servi tel quel, dans le cadre étroit d'un
       * téléphone, parce que c'est là que la salle regarde.
       */}
      {/**
       * ⚠️ UN SEUL APERÇU, ET C'EST CELUI DU PARTICIPANT. Il y en a eu deux un
       * moment, l'autre montrant la salle — sans objet depuis que les deux
       * surfaces servent le MÊME module : la salle n'est que la même chose en
       * grand, sans la vidéo. Un second aperçu ne montrait donc rien de plus.
       */}
      <div className="rgp__vue">
        {/**
         * ═══ LA DIFFUSION, EN HAUT À GAUCHE, AU-DESSUS DE L'APERÇU (#372) ═══
         *
         * ⚠️ ELLE ÉTAIT UN BLOC PLEINE LARGEUR EN TÊTE DES COMMANDES, avec son titre,
         * son compteur sur une ligne et trois grands boutons : la place de deux
         * activités, pour un geste qu'on fait deux fois par session. Une barre serrée
         * au-dessus de ce que voit la salle la garde sous l'œil — la colonne suit le
         * défilement — et la sort du mode : elle se lit aussi pendant qu'on trie les SMS.
         *
         * ⚠️ « ON AIR » QUAND ELLE EST EN COURS, en rouge, comme le voyant d'un
         * plateau. Un bouton enfoncé parmi trois se lit ; un voyant rouge se voit.
         */}
        <section className="rgd" data-etat={live.state.broadcast}>
          <div className="rgd__t">
            <h3>Diffusion</h3>
            {live.state.broadcast === "live" ? (
              <span className="rgd__air" lang="en">
                ON AIR
              </span>
            ) : null}
            {/* ⚠️ LE NOMBRE EST ICI PARCE QUE C'EST ICI QU'ON LE CHERCHE. Il compte
                tout le monde qui a la page ouverte, vous compris. Les écrans de salle
                exceptés : un vidéoprojecteur n'est pas une personne. */}
            {/* ⚠️ ZÉRO ET « ON NE SAIT PAS » NE SE DISENT PAS PAREIL. `viewers: null`
                veut dire que le serveur n'a pas de canal pour compter (voir
                `types.ts`), et l'écrire « personne n'est connecté » annonçait une
                salle vide à un organisateur qui en a une pleine devant lui. Sans
                chiffre, on ne dit rien. */}
            {typeof live.viewers === "number" ? (
              <span className="rgd__n">
                {live.viewers} personne{live.viewers > 1 ? "s" : ""} en ligne, vous
                comprise
              </span>
            ) : null}
          </div>

          <div className="rg__seg" role="group" aria-label="État de la diffusion">
            {(
              [
                ["before", "Avant"],
                ["live", "En cours"],
                ["ended", "Terminée"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={live.state.broadcast === value}
                onClick={() => setState.mutate({ broadcast: value })}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ⚠️ DIT À CELUI QUI PILOTE, pas caché dans une console de navigateur.
              Sans adresse de canal, tout marche mais rien n'est poussé : les écrans
              suivent avec jusqu'à trente secondes de retard, et le compteur ne peut
              pas compter. Se découvre autrement pendant une plénière, quand on lance
              un sondage et que la salle ne le voit pas tout de suite. */}
          {!getConfig().wsUrl ? (
            <p className="hint">
              Le canal temps réel n'est pas configuré sur cette installation : les
              écrans se mettent à jour toutes les trente secondes au lieu d'être
              poussés, et les spectateurs ne sont pas comptés.
            </p>
          ) : null}

          {/* ⚠️ LANCER NE MET PAS LA DIFFUSION « EN COURS », et c'est voulu : le
              serveur ne touche pas à la diffusion quand on pousse une activité
              (`setPhase.js`). Mais la salle et les téléphones ne montrent une activité
              que pendant la diffusion : lancée sur « Avant » ou « Terminée », elle est
              ouverte, la liste la dit « Ouverte », et personne ne la voit. L'aperçu ne
              le trahit pas assez — il montre le texte d'attente, comme avant le clic.
              Donc on le dit, là où se trouve le geste qui répare (bug #261). */}
          {live.activity && live.state.broadcast !== "live" ? (
            <p className="live__err" role="alert">
              {live.activity.title || live.activity.content
                ? `« ${live.activity.title || live.activity.content} »`
                : "L'activité"}{" "}
              est lancée, mais personne ne la voit :{" "}
              {live.state.broadcast === "ended"
                ? "la diffusion est terminée"
                : "la diffusion n'a pas commencé"}
              . Passez la diffusion sur « En cours » pour l'afficher en salle et sur
              les téléphones.
            </p>
          ) : null}
        </section>

        <span className="rgp__lab">Ce que voit un participant</span>
        <div className="rgp__tel">
          <LiveView
            live={live}
            sequenceId={sequenceId}
            isPending={isPending}
            isError={isError}
            preview
          />
        </div>

        {/**
         * ═══ LE CODE DE L'ÉCRAN DE SALLE, SOUS L'APERÇU (#375, #452) ═══
         *
         * ⚠️ IL ÉTAIT DANS LES RÉGLAGES DE LA RÉGIE, derrière « Configurer le site » :
         * celui qui installe le poste de la salle a la régie ouverte, pas le panneau, et
         * le code n'écrit rien dans la configuration du site — il vit dans l'état du
         * direct, par ses propres routes. Il est donc ici, et nulle part ailleurs.
         *
         * ⚠️ À GAUCHE, EN BARRE SERRÉE COMME LA DIFFUSION (#452). Il a été un bloc
         * pleine largeur en bas des commandes, code en 30 px : la place d'une activité
         * pour un geste qu'on fait une fois, avant que la salle arrive. Sous ce que voit
         * un participant, il ne pousse plus les commandes qu'on pilote, et il reste hors
         * des deux modes : atteignable pendant qu'on trie les SMS.
         */}
        <section className="rge">
          <h3>Écran de la salle</h3>
          <ScreenCodeManager />
        </section>
      </div>

      <div className="rg">
        <div className="ttl">
          <h2>Régie</h2>
          <p>Ce que vous décidez ici s'affiche en salle et sur les téléphones.</p>
        </div>
        {/**
         * ═══ DEUX MODES : LES ACTIVITÉS, OU LES SMS ═══
         *
         * ⚠️ UN MODE, PAS UN BLOC DE PLUS SOUS LES AUTRES. Une salle qui répond par SMS
         * en envoie des centaines : posée sous le pilotage, la liste repoussait les
         * commandes hors de l'écran — la leçon de la file de modération. Le compte, lui,
         * reste sur l'onglet, pour qu'on voie arriver les SMS depuis les activités.
         */}
        <div className="rg__seg" role="group" aria-label="Mode de la régie">
          {(
            [
              ["activities", "Activités"],
              ["sms", "SMS"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
            >
              {value === "sms" && sms.data ? `${label} (${sms.data.total})` : label}
            </button>
          ))}
        </div>

        {mode === "sms" ? (
          <SmsInbox live={live} />
        ) : (
          <>
            <section className="rg__b">
              <div className="rg__t">
                {/* « Conducteur » (2026-10-09), à la place de « Les activités » : le mot
                    de la régie pour la suite ordonnée de ce qu'on joue. */}
                <h3>Conducteur</h3>
                {/**
                 * ⚠️ « RÉINITIALISER LA RÉPÉTITION » (2026-09-30 : « on fait des dry run »).
                 * Après avoir joué les activités devant l'équipe, tout revient comme avant
                 * l'arrivée de la salle, d'un geste : les réponses effacées, les activités
                 * préparées, la diffusion « Avant ». Seulement quand il y a de quoi : sur un
                 * direct vierge, le bouton laisserait croire qu'il reste quelque chose.
                 */}
                <span className="rg__acts">
                  {rehearsal.needed ? (
                    <Button variant="ghost" onClick={() => setConfirmRehearsal(true)}>
                      Réinitialiser la répétition
                    </Button>
                  ) : null}
                  <Button onClick={() => setPreparer(true)}>Créer une activité</Button>
                </span>
              </div>

              {/* Une option d'AFFICHAGE des résultats, donc elle vit avec eux. Elle
                  était rangée avec l'écran de salle, qui n'existe plus comme réglage. */}
              <label className="rg__c">
                <input
                  type="checkbox"
                  checked={!!live?.state.hideVoteCounts}
                  onChange={(e) =>
                    setState.mutate({ hideVoteCounts: e.target.checked })
                  }
                />
                Montrer les parts sans le nombre de votants
              </label>

              {/**
               * ═══ CE QUI RESTE À FAIRE, ET CE QUI EST DERRIÈRE ═══
               *
               * ⚠️ LES DEUX GROUPES SONT ATTEIGNABLES, aucun n'est jeté. Une activité
               * terminée se relit (ses réponses, ses résultats), se rouvre et se vide :
               * la masquer pour de bon aurait remplacé une liste encombrée par une liste
               * incomplète. Le compte sur l'onglet dit ce qu'il y a derrière.
               */}
              {activities?.length ? (
                <div className="rg__filtres">
                  <div className="rg__seg" role="group" aria-label="Activités affichées">
                    {(
                      [
                        [false, "Pas terminées", groups.todo.length],
                        [true, "Terminées", groups.done.length],
                      ] as const
                    ).map(([value, label, count]) => (
                      <button
                        key={label}
                        type="button"
                        aria-pressed={showDone === value}
                        onClick={() => setShowDone(value)}
                      >
                        {label} ({count})
                      </button>
                    ))}
                  </div>

                  {/**
                   * ⚠️ SEULEMENT LES TYPES QUE L'ÉVÉNEMENT PORTE, et seulement s'il en a
                   * plusieurs. Proposer les six d'office fait choisir « Nuage de mots » à
                   * qui n'en a aucun, donc lire une liste vide comme une console cassée ;
                   * et un événement qui n'a que des sondages n'a rien à filtrer.
                   */}
                  {types.length > 1 ? (
                    <label className="rg__type">
                      Type
                      <select
                        value={shownType}
                        onChange={(e) => setType(e.target.value as ActivityType | "")}
                      >
                        <option value="">Tous</option>
                        {types.map((t) => (
                          <option key={t} value={t}>
                            {TYPE_LABELS[t] ?? t}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
              ) : null}

              {/* ⚠️ « AUCUNE » EST UNE AFFIRMATION, pas un état par défaut. Une lecture
                  en échec laisse la liste vide, et l'écran annonçait alors qu'un
                  événement n'a rien de préparé alors qu'il n'en sait rien. */}
              {activitiesFailed ? (
                <p className="empty">
                  La liste des activités n'a pas pu être chargée.
                </p>
              ) : !activities?.length ? (
                <p className="empty">
                  Aucune activité préparée. Créez-en une pour commencer.
                </p>
              ) : !shown.length ? (
                /* ⚠️ « VIDE ICI » N'EST PAS « VIDE », et il y a maintenant deux façons
                   d'être vide ici. Réutiliser le message du dessus annoncerait un
                   événement sans rien de préparé à quelqu'un qui vient de tout terminer ;
                   annoncer « tout est terminé » sous un filtre de type serait faux deux
                   fois, puisque c'est le filtre qui vide la liste. */
                <p className="empty">
                  {shownType
                    ? `Aucune activité « ${TYPE_LABELS[shownType] ?? shownType} » dans ce groupe.`
                    : showDone
                      ? "Aucune activité terminée pour l'instant."
                      : "Tout est terminé. Rouvrez-en une, ou créez-en une nouvelle."}
                </p>
              ) : (
                <div className="clist">
                  {shown.map((activity, rank) => (
                    <ActivityRow
                      key={activity.id}
                      activity={activity}
                      active={live?.state.activityId === activity.id}
                      /**
                       * ⚠️ LE COMPTE DE LA CHARGE POUR LA SEULE LIGNE À L'ÉCRAN. La liste
                       * ne se relit qu'aux gestes, donc son « 0 réponse » restait affiché
                       * pendant qu'une salle répondait. La charge du direct, elle, arrive
                       * à chaque réponse — elle était déjà là, on ne s'en servait pas.
                       *
                       * Les autres lignes gardent le compte de la liste, et c'est exact :
                       * une seule activité est ouverte à la fois (le serveur le tient),
                       * donc une seule peut voir son compte bouger.
                       */
                      liveCount={
                        live?.state.activityId === activity.id
                          ? live?.answerCount
                          : undefined
                      }
                      /* Même règle que le compte : le direct pour la ligne à l'écran,
                         la liste pour les autres (voir `rowResults`). */
                      results={rowResults(activity, live)}
                      /* ⚠️ LE RANG EST CELUI DU GROUPE AFFICHÉ, pas celui de l'ordre
                         complet : les flèches disent « je ne peux plus monter » à propos
                         de ce qu'on voit. Ce qui PART au serveur reste l'ordre entier. */
                      rank={rank}
                      total={shown.length}
                      reorder={(sens) =>
                        reorder.mutate(
                          movedAmong(
                            activities.map((a) => a.id),
                            shown.map((a) => a.id),
                            activity.id,
                            sens,
                          ),
                        )
                      }
                      reordering={reorder.isPending}
                      /* La file d'attente n'est connue que pour l'activité à l'écran :
                         c'est la seule dont le direct porte les questions. */
                      waiting={
                        questionsOnScreen && live.state.activityId === activity.id
                          ? waiting
                          : undefined
                      }
                      onEdit={() => setEditing(activity)}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        <Modal
          open={preparer}
          title="Préparer une activité"
          onClose={() => setPreparer(false)}
        >
          <ActivityEditor onCreated={() => setPreparer(false)} />
        </Modal>

        {/**
         * ⚠️ MONTÉE SEULEMENT QUAND ELLE SERT, et remontée à chaque activité. La
         * boîte garde l'état de son formulaire : une seule instance laissée en place
         * rouvrirait la suivante avec les réponses de la précédente. La clé force le
         * remontage, c'est ce qui rend le préremplissage juste.
         */}
        {editing ? (
          <Modal
            key={editing.id}
            open
            title="Modifier l'activité"
            onClose={() => setEditing(null)}
          >
            <ActivityEditor
              editing={editing}
              onCreated={() => setEditing(null)}
            />
          </Modal>
        ) : null}

        {/**
         * ⚠️ LA QUESTION DIT CE QUI EST PERDU ET CE QUI RESTE. Les réponses effacées ne
         * sont archivées nulle part ; le reste — activités, choix, ordre, réglages, SMS
         * reçus — ne bouge pas, et c'est ce qui distingue ce geste d'une suppression.
         */}
        <Confirm
          open={confirmRehearsal}
          title="Réinitialiser la répétition ?"
          message={`${
            rehearsal.answers === 0
              ? "Aucune réponse à effacer."
              : rehearsal.answers === 1
                ? "L'unique réponse sera effacée définitivement."
                : `Les ${rehearsal.answers} réponses seront effacées définitivement.`
          } Toutes les activités redeviendront préparées (fermées, résultats masqués, retirées de l'écran) et la diffusion reviendra à « Avant ». Rien n'est supprimé : activités, choix, ordre, réglages et SMS reçus sont conservés.`}
          confirmLabel="Réinitialiser"
          onConfirm={() =>
            resetAll.mutate(undefined, { onSettled: () => setConfirmRehearsal(false) })
          }
          busy={resetAll.isPending}
          onCancel={() => setConfirmRehearsal(false)}
          /* Un clic de trop se rattrape : c'est la sortie qui est mise en avant. */
          emphasis="cancel"
        />
      </div>
    </section>
  );
}

function ActivityRow({
  activity,
  active,
  rank,
  total,
  reorder,
  reordering,
  liveCount,
  results,
  waiting,
  onEdit,
}: {
  activity: ControlActivity;
  active: boolean;
  /** Le compte que porte la charge du direct, quand cette ligne est à l'écran. */
  liveCount?: number;
  /** Ses résultats, montrés ou non à la salle, ou rien pour un type qui n'en a pas. */
  results: Results | null;
  /** Sa place dans la liste, à partir de zéro. */
  rank: number;
  total: number;
  reorder: (sens: -1 | 1) => void;
  reordering: boolean;
  /** Les questions en attente de verdict, quand cette ligne est à l'écran. */
  waiting?: number;
  onEdit: () => void;
}) {
  const { context } = useParams();
  const phase = useSetPhase(activity.id);
  const remove = useRemoveActivity();
  const can = gesturesFor(activity);
  const { primary: principal, others: autres } = phaseGestures(activity);
  const nom = activity.title || activity.content;

  /* ⚠️ ON DEMANDE UNE FOIS quand l'activité contient des réponses : la suppression
     emporte les votes avec elle, et ce bouton est voisin de « Clore » sur un écran
     qu'on pilote sous pression. Le serveur exige `force` pour la même raison. */
  /* Le direct fait foi quand il parle : il est plus frais que la liste. */
  const count = liveCount ?? activity.answerCount;

  const [aConfirmer, setAConfirmer] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const reset = useResetLive();
  /* Les résultats dépliés sous la ligne. Une vue de la régie : rien ne part au
     serveur, la salle n'en sait rien.
     ⚠️ DÉPLIÉS POUR L'ACTIVITÉ QU'ON OUVRE, REPLIÉS POUR LES AUTRES (#332) : voir
     `unfoldedAfter`. Suivi pendant le rendu, pas dans un effet, sinon la ligne qu'on
     vient de lancer se dessine d'abord repliée. */
  const play: RowPlay = { open: activity.phase === "open", onScreen: active };
  const [seeResults, setSeeResults] = useState(() => unfoldedAtFirst(play));
  const [lastPlay, setLastPlay] = useState(play);
  if (play.open !== lastPlay.open || play.onScreen !== lastPlay.onScreen) {
    setLastPlay(play);
    setSeeResults((unfolded) => unfoldedAfter(lastPlay, play, unfolded));
  }
  const resultsId = useId();

  return (
    <div className="rga" data-active={active ? "1" : undefined}>
      {/**
       * ⚠️ DEUX FLÈCHES, PAS UN GLISSÉ. La liste se range à la préparation, parfois
       * une main sur le clavier, et un glissé-déposé ne se fait ni au clavier ni
       * d'une main sûre. Deux boutons, qui disent aussi où l'on est : la première
       * ligne ne monte pas, la dernière ne descend pas.
       */}
      <span className="rga__o">
        <button
          type="button"
          aria-label={`Monter « ${activity.title || activity.content} »`}
          disabled={rank === 0 || reordering}
          onClick={() => reorder(-1)}
        >
          ↑
        </button>
        <button
          type="button"
          aria-label={`Descendre « ${activity.title || activity.content} »`}
          disabled={rank === total - 1 || reordering}
          onClick={() => reorder(1)}
        >
          ↓
        </button>
      </span>

      <span className="rga__t">
        <b>{activity.title || activity.content}</b>
        {/**
         * ═══ L'ÉTAT EN PASTILLES, PLUS EN LIGNE DE POINTS ═══
         *
         * ⚠️ TROIS FAITS DE NATURES DIFFÉRENTES ÉTAIENT SÉPARÉS PAR DES POINTS, au
         * même poids et dans le même gris : la phase, un réglage d'affichage et un
         * nombre. « Close · résultats montrés · 2 réponses » se lit en entier ou ne
         * se lit pas. Trois pastilles se balayent.
         *
         * ⚠️ UNE SEULE PORTE UNE COULEUR, celle qui dit « ouverte ». Les quatre
         * phases colorées feraient un confetti où plus rien ne ressort, alors que ce
         * qu'on cherche dans une liste de dix c'est celle qui accepte des réponses en
         * ce moment. Le serveur garantit qu'il n'y en a jamais deux.
         */}
        <span className="rga__meta">
          {/* ⚠️ TERMINÉE PREND LA PLACE DE LA PHASE, elle ne s'ajoute pas à côté :
              une activité terminée est close par construction, et « Close ·
              terminée » ferait lire deux états là où il n'y en a qu'un. */}
          <span
            className="rga__st"
            data-etat={activity.endedAt ? "ended" : activity.phase}
          >
            {activity.endedAt
              ? "Terminée"
              : (PHASE_LABELS[activity.phase] ?? activity.phase)}
          </span>
          {/* ⚠️ ET LA MENTION SUIT LA MÊME RÈGLE QUE LE BOUTON. Sur un nuage ou des
              questions, `showResults` peut valoir vrai en base — écrit par l'ancien
              bouton — sans rien gouverner : l'afficher annoncerait un état que
              personne ne peut ni voir ni défaire. */}
          {can.hasResults && activity.showResults ? (
            <span className="rga__st">Résultats montrés</span>
          ) : null}
          <span className="rga__st">
            {count} réponse{count > 1 ? "s" : ""}
          </span>
          {/**
           * ═══ VOIR LES RÉSULTATS, SANS LES MONTRER (#284) ═══
           *
           * ⚠️ LA RÉGIE NE LES LISAIT QUE DANS L'APERÇU DU PARTICIPANT, donc après
           * « Montrer les résultats » — les dévoiler à toute la salle pour savoir où en
           * est le vote — et pour la seule activité à l'écran. Ici, chaque ligne
           * déplie les siens, et ce geste n'envoie rien.
           *
           * ⚠️ AVEC LES FAITS, PAS AVEC LES COMMANDES. La barre du dessous agit sur la
           * salle ; « Voir » posé à côté de « Montrer » et « Cacher » se serait lu
           * comme un troisième geste sur la salle. Un dépliant, parmi les pastilles
           * qui décrivent la ligne, dit qu'on regarde.
           */}
          {can.hasResults && results ? (
            <button
              type="button"
              className="rga__voir"
              aria-expanded={seeResults}
              aria-controls={resultsId}
              onClick={() => setSeeResults((open) => !open)}
            >
              <span className="rga__ch" aria-hidden="true" />
              Voir les résultats
            </button>
          ) : null}
        </span>
      </span>

      {/**
       * ═══ LE TYPE, EN ÉTIQUETTE ═══
       *
       * ⚠️ IL ÉTAIT LE PREMIER MOT D'UNE LIGNE DE MÉTA séparée par des points, entre
       * la phase, les résultats et le compte de réponses. C'est pourtant ce qu'on
       * cherche en parcourant la liste — « où est mon quiz ? » — et il se lisait au
       * même poids que le reste, en 11 px gris. En haut à droite de la tuile, il se
       * balaye d'un coup d'œil sans lire une seule ligne.
       */}
      <span className="rga__tag" data-type={activity.type}>
        {TYPE_LABELS[activity.type] ?? activity.type}
      </span>

      {reset.isError || remove.isError ? (
        <small className="rga__note live__err" role="alert">
          {refusalOf(reset.error ?? remove.error)}
        </small>
      ) : null}

      {reset.data ? (
        <small className="rga__note">
          {reset.data.deletedAnswers === 1
            ? "1 réponse effacée."
            : `${reset.data.deletedAnswers} réponses effacées.`}
        </small>
      ) : null}

      {phase.data && phase.data.alsoClosed > 0 ? (
        <small className="rga__note">
          {phase.data.alsoClosed === 1
            ? "L'activité qui était ouverte a été close."
            : `${phase.data.alsoClosed} activités qui étaient ouvertes ont été closes.`}
        </small>
      ) : null}

      {/**
       * ═══ UN GESTE MIS EN AVANT, LE RESTE DERRIÈRE ⋯ ═══
       *
       * ⚠️ CETTE LIGNE A PORTÉ SIX BOUTONS DE MÊME POIDS (modifier, rouvrir,
       * terminer, cacher les résultats, vider, supprimer). Six cibles identiques ne
       * se lisent pas sur un écran qu'on pilote sous pression : on cherche, et
       * pendant qu'on cherche la salle attend. Restent visibles les deux qu'on
       * enchaîne réellement pendant une session — l'étape suivante de l'activité, et
       * l'interrupteur de résultats. Le reste est rare, il est dans le menu.
       *
       * ⚠️ ET LA DESTRUCTION RESTE À PART, comme décidé : dernière, derrière un
       * filet, en couleur d'alerte. La ranger avec le pilotage reviendrait à poser
       * « Supprimer » à côté de « Clore ».
       */}
      <div className="rga__f">
        <span className="rga__a">
          {/**
           * ⚠️ UNE SEULE ACTION MISE EN AVANT, ET C'EST LA PHASE QUI LA DÉSIGNE. Une
           * activité préparée se lance, une ouverte se clôt, une close se termine,
           * une terminée se rouvre. Sans ce choix, « Rouvrir » s'affichait en
           * PRINCIPAL sur une activité close : le geste rare tenait la place du
           * geste courant, sur la ligne qu'on vient piloter.
           */}
          {principal ? (
            <Button
              loading={phase.isPending}
              aria-label={`${principal.label} « ${nom} »`}
              onClick={() => phase.mutate(principal.body)}
            >
              {principal.label}
            </Button>
          ) : null}

          {/**
           * ⚠️ TROIS ÉTATS, PAS DEUX. C'était un ternaire : « montrer » quand on peut
           * montrer, « cacher » SINON. Donc une activité qui ne peut faire ni l'un ni
           * l'autre — un classement, qui ne se répond pas — affichait quand même
           * « Cacher les résultats ». `canHide` existait et n'était jamais lu.
           */}
          {can.canShow ? (
            <Button
              variant="ghost"
              loading={phase.isPending}
              onClick={() => phase.mutate({ showResults: true })}
            >
              Montrer les résultats
            </Button>
          ) : null}
          {can.canHide ? (
            <Button
              variant="ghost"
              loading={phase.isPending}
              onClick={() => phase.mutate({ showResults: false })}
            >
              Cacher les résultats
            </Button>
          ) : null}

          {/**
           * ═══ LA MODÉRATION, SUR LA LIGNE DE SES QUESTIONS (#451) ═══
           *
           * ⚠️ SON SEUL CHEMIN ÉTAIT EN BAS DE LA RÉGIE, sous toutes les activités :
           * modérer celle qu'on vient de lancer voulait dire faire défiler toute la
           * session. Ici, dans les gestes qu'on enchaîne, parce que c'en est un — des
           * questions, on ne montre pas de résultats, on les trie.
           *
           * ⚠️ FILTRÉE SUR CETTE ACTIVITÉ, et TOUJOURS DANS UN NOUVEL ONGLET (#373) :
           * ouverte dans le même, la modération remplaçait la régie, et piloter la
           * salle voulait dire revenir en arrière au milieu d'une session. Le jeton est
           * en `localStorage` et l'alias dans le chemin : le nouvel onglet arrive
           * connecté, sur le même événement.
           *
           * ⚠️ C'EST DÉSORMAIS LE SEUL CHEMIN (#455) : le bloc « Questions du public »
           * du bas de la régie faisait doublon avec ces boutons, il est retiré. Toutes
           * les activités à la fois se relisent depuis la modération elle-même.
           */}
          {activity.type === "questions" ? (
            <Link
              className="btn btn--ghost"
              to={withContext(moderationPath(activity.id), context)}
              target="_blank"
              rel="noopener"
              aria-label={`Modérer les questions de « ${nom} »${
                waiting ? `, ${waiting} en attente` : ""
              }`}
            >
              {waiting ? `Modérer (${waiting})` : "Modérer"}
            </Link>
          ) : null}
        </span>

        <Menu label={`Autres actions sur « ${nom} »`}>
          {/* Corriger une faute de frappe est un geste de préparation : le serveur
              apparie les choix par identifiant, donc les votes survivent. */}
          <MenuItem onClick={onEdit}>Modifier</MenuItem>

          {/* Les gestes de phase que le bouton principal n'a pas pris. */}
          {autres.map((geste) => (
            <MenuItem key={geste.key} onClick={() => phase.mutate(geste.body)}>
              {geste.label}
            </MenuItem>
          ))}

          <MenuSep />

          {/* ⚠️ « RÉINITIALISER » NE S'OFFRE QUE SI L'ACTIVITÉ A ÉTÉ JOUÉE (`hasBeenPlayed`) :
              sur une activité vierge il ne ferait rien. Il remplace « Vider les
              réponses », qui la laissait close, résultats montrés (2026-09-30). */}
          {hasBeenPlayed(activity, count) ? (
            <MenuItem danger onClick={() => setConfirmReset(true)}>
              Réinitialiser
            </MenuItem>
          ) : null}
          <MenuItem
            danger
            onClick={() => {
              /* Sans réponse à perdre, la question ne se pose pas : demander pour
                 une activité vide apprend à cliquer « oui » sans lire. */
              if (count > 0) setAConfirmer(true);
              else remove.mutate({ id: activity.id });
            }}
          >
            Supprimer
          </MenuItem>
        </Menu>
      </div>

      {/* ⚠️ SOUS LA BARRE, PAS ENTRE LE TITRE ET ELLE : déplier ne doit pas déplacer
          « Clore » sous le doigt de celui qui pilote. */}
      {can.hasResults && results ? (
        <div className="rga__res" id={resultsId} hidden={!seeResults}>
          {/* Ce qu'on regarde ici n'est pas forcément ce que la salle voit : le dire,
              sinon on hésite à déplier de peur de dévoiler. Montrés, la pastille
              « Résultats montrés » le dit déjà au-dessus. */}
          {!activity.showResults ? (
            <small>Visibles ici seulement : la salle ne les voit pas.</small>
          ) : null}
          <Tally activity={activity} results={results} />
        </div>
      ) : null}

      {/**
       * ⚠️ UNE VRAIE FENÊTRE, PLUS UN BOUTON QUI CHANGE DE TEXTE. Le second temps
       * était « Confirmer la remise à zéro » à la place du bouton : dans un menu qui
       * se referme à l'activation, ce second temps n'a plus où vivre. Et la question
       * y gagne — elle dit ce qui va être perdu, et ce qui est effacé n'est archivé
       * nulle part.
       */}
      <Confirm
        open={confirmReset}
        title={`Réinitialiser « ${nom} » ?`}
        message={`${
          count === 0
            ? "Elle n'a aucune réponse à effacer."
            : count === 1
              ? "Son unique réponse sera effacée définitivement."
              : `Ses ${count} réponses seront effacées définitivement.`
        } Elle redeviendra préparée : fermée, résultats masqués, retirée de l'écran. L'activité, ses choix et sa place dans la liste sont conservés.`}
        confirmLabel="Réinitialiser"
        onConfirm={() =>
          reset.mutate(activity.id, { onSettled: () => setConfirmReset(false) })
        }
        busy={reset.isPending}
        onCancel={() => setConfirmReset(false)}
        /* Ce garde-fou existe pour rattraper un clic de trop : c'est la sortie qui
           est mise en avant, pas la destruction. */
        emphasis="cancel"
      />

      <Confirm
        open={aConfirmer}
        title="Supprimer cette activité ?"
        message={`« ${nom} » et ${
          count === 1 ? "son unique réponse" : `ses ${count} réponses`
        } seront supprimées définitivement.`}
        confirmLabel="Supprimer"
        onConfirm={() =>
          remove.mutate(
            { id: activity.id, force: true },
            { onSettled: () => setAConfirmer(false) },
          )
        }
        busy={remove.isPending}
        onCancel={() => setAConfirmer(false)}
        emphasis="cancel"
      />
    </div>
  );
}
